import { createContext, useState, useEffect } from "react";
import logo from "../assets/logo-onresearch-white.png";
export const GlobalStylesContext = createContext(null);

const THEME_STORAGE_KEY = 'survey-theme-preference';
const THEME_MEDIA_QUERY = '(prefers-color-scheme: dark)';
const SPECIAL_THEME_SID = '26INT006';

const hasSpecialThemeSid = () => {
  if (typeof window === 'undefined') return false;

  return new URLSearchParams(window.location.search).get('sid')?.includes(SPECIAL_THEME_SID);
};

export const GlobalStylesProvider = ({ children }) => {
  const isSpecialTheme = hasSpecialThemeSid();

  // Legacy color settings
  const [headerColor, setHeaderColor] = useState("#5BC5CD");
  const [logoPath, setLogoPath] = useState(logo);
  const [buttonBgColorNormal, setButtonBgColorNormal] = useState("#F58233");
  const [buttonBgColorHover, setButtonBgColorHover] = useState("#DF7706");
  const [buttonBorderRadius, setButtonBorderRadius] = useState("20px");
  
  // Logo configuration for light/dark themes
  const [logoLightMode, setLogoLightMode] = useState("https://onrsurveys.app/ONR_Surveys_purple.png");
  const [logoDarkMode, setLogoDarkMode] = useState("https://onrsurveys.app/ONR_Surveys_white.png");
  
  // New theme system - configurable purple colors
  const [accentPrimary, setAccentPrimary] = useState("#9333ea"); // purple-600
  const [accentPrimaryHover, setAccentPrimaryHover] = useState("#7e22ce"); // purple-700
  const [accentLight, setAccentLight] = useState("#f3e8ff"); // purple-50
  const [accentDark, setAccentDark] = useState("#581c87"); // purple-900
  
  // Theme mode state (light, dark, or system)
  const [themeMode, setThemeMode] = useState(() => {
    // Initialize from localStorage or default to 'light'
    if (typeof window !== 'undefined') {
      return localStorage.getItem(THEME_STORAGE_KEY) || 'light';
    }
    return 'light';
  });
  
  // Determine if we should use dark theme
  const [isDark, setIsDark] = useState(false);
  
  // Update isDark based on themeMode
  useEffect(() => {
    if (isSpecialTheme) {
      setIsDark(false);
      return;
    }

    if (themeMode === 'system') {
      // Check system preference
      const mediaQuery = window.matchMedia(THEME_MEDIA_QUERY);
      setIsDark(mediaQuery.matches);
      
      // Listen for system preference changes
      const handler = (e) => setIsDark(e.matches);
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      setIsDark(themeMode === 'dark');
    }
  }, [themeMode, isSpecialTheme]);
  
  // Toggle theme function
  const toggleTheme = () => {
    if (isSpecialTheme) {
      setIsDark(false);
      return;
    }

    const newMode = themeMode === 'dark' ? 'light' : 'dark';
    setThemeMode(newMode);
    localStorage.setItem(THEME_STORAGE_KEY, newMode);
  };
  
  // Set specific theme mode
  const setTheme = (mode) => {
    if (isSpecialTheme) {
      setIsDark(false);
      return;
    }

    setThemeMode(mode);
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  };
  
  return (
    <GlobalStylesContext.Provider
      value={{
        // Legacy values
        headerColor,
        setHeaderColor,
        logoPath,
        setLogoPath,
        buttonBgColorNormal,
        setButtonBgColorNormal,
        buttonBgColorHover,
        setButtonBgColorHover,
        buttonBorderRadius,
        setButtonBorderRadius,
        
        // Logo configuration
        logoLightMode,
        setLogoLightMode,
        logoDarkMode,
        setLogoDarkMode,
        
        // New theme system
        accentPrimary,
        setAccentPrimary,
        accentPrimaryHover,
        setAccentPrimaryHover,
        accentLight,
        setAccentLight,
        accentDark,
        setAccentDark,
        themeMode,
        setTheme,
        toggleTheme,
        isDark,
        isSpecialTheme,
      }}
    >
      {children}
    </GlobalStylesContext.Provider>
  );
};
